/* Cookie consent state manager for MyHandyHub.
   Complies with UK GDPR + PECR:
   - No non-essential storage/scripts fire until the user makes a decision.
   - Analytics and Marketing are separate opt-ins.
   - "Reject all" is equally prominent to "Accept all".
   - Choice is persisted with a version + timestamp so we can re-prompt when policy changes.

   Wires up:
   - Google Consent Mode v2 (`gtag('consent', 'update', ...)`)
   - Google Analytics 4 (loads gtag.js only after analytics consent)
   - PostHog (opts in/out of capturing based on analytics consent)
   - MyHandyHub's own /api/track pings (only if analytics consent)
*/

export const CONSENT_VERSION = 1;
const STORAGE_KEY = `mhh_consent_v${CONSENT_VERSION}`;
export const CONSENT_EVENT = "mhh:consent-change";
export const OPEN_BANNER_EVENT = "mhh:open-cookie-banner";

const DEFAULT_PREFS = {
  essential: true, // always on — required for the site to work
  analytics: false,
  marketing: false,
};

let _cachedPrefs = null;

/** Read stored consent, or null if the user hasn't decided yet. */
export function getConsent() {
  if (_cachedPrefs) return _cachedPrefs;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    _cachedPrefs = { ...DEFAULT_PREFS, ...parsed.prefs };
    return _cachedPrefs;
  } catch (e) {
    return null;
  }
}

/** True if the user has made a decision (either way). Banner is hidden when true. */
export function hasConsentDecision() {
  return getConsent() !== null;
}

/** Persist a decision and apply it live. */
export function saveConsent(prefs) {
  const merged = { ...DEFAULT_PREFS, ...prefs, essential: true };
  const payload = {
    v: CONSENT_VERSION,
    prefs: merged,
    decided_at: new Date().toISOString(),
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { /* private mode */ }
  _cachedPrefs = merged;
  applyConsent(merged);
  try { window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: merged })); } catch (e) { /* no-op */ }
}

/** Convenience helpers used by the banner buttons. */
export function acceptAll() { saveConsent({ essential: true, analytics: true, marketing: true }); }
export function rejectAll() { saveConsent({ essential: true, analytics: false, marketing: false }); }

/** Consumers can dispatch this to re-open the banner from a "Cookie preferences" footer link. */
export function openPreferences() {
  try { window.dispatchEvent(new CustomEvent(OPEN_BANNER_EVENT)); } catch (e) { /* no-op */ }
}

/** Apply the given consent to all downstream trackers. Called on saveConsent + on app boot. */
export function applyConsent(prefs) {
  if (typeof window === "undefined") return;

  // === Google Consent Mode v2 ===
  try {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function () { window.dataLayer.push(arguments); };
    }
    window.gtag("consent", "update", {
      ad_storage: prefs.marketing ? "granted" : "denied",
      ad_user_data: prefs.marketing ? "granted" : "denied",
      ad_personalization: prefs.marketing ? "granted" : "denied",
      analytics_storage: prefs.analytics ? "granted" : "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    });
  } catch (e) { /* no-op */ }

  // === Load Google Analytics 4 gtag.js on first analytics-consent ===
  const gaId = process.env.REACT_APP_GA4_ID || "";
  if (prefs.analytics && gaId && !window.__mhh_ga_loaded) {
    window.__mhh_ga_loaded = true;
    try {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(s);
      window.gtag("js", new Date());
      window.gtag("config", gaId, { anonymize_ip: true });
    } catch (e) { /* no-op */ }
  }

  // === PostHog ===
  try {
    if (window.posthog) {
      if (prefs.analytics) {
        if (typeof window.posthog.opt_in_capturing === "function") window.posthog.opt_in_capturing();
      } else if (typeof window.posthog.opt_out_capturing === "function") {
        window.posthog.opt_out_capturing();
      }
    }
  } catch (e) { /* no-op */ }
}

/** Fired on initial page load: applies stored consent OR keeps everything denied until decision. */
export function bootConsent() {
  const stored = getConsent();
  if (stored) {
    applyConsent(stored);
  } else {
    // No decision yet — everything stays denied by the Consent Mode default
    applyConsent(DEFAULT_PREFS);
  }
}
